/**
 * BLRRD Path Editor — stroke-fill.js  (Phase 4.G.vector, Wave 12.2)
 *
 * Three rasterisation operations on a flattened Bezier path:
 *
 *   1. fillPath   — scanline-fill the closed polygon (REUSES the Pineda
 *                   1988 odd-even scanline rasteriser from
 *                   src/selection/lasso.js — we do NOT re-implement it).
 *   2. strokePath — sweep a variable-width quad strip along the polyline
 *                   normals and rasterise each quad with the SAME lasso
 *                   scanline. A stroke is just a sequence of overlapping
 *                   filled quadrilaterals.
 *   3. pathToMask — rasterise the closed path's fill into a Float32
 *                   coverage mask and combine it into a target mask via
 *                   src/selection/combine.js (Path → Selection).
 *
 * Colour handling: stroke + fill blend a single ACEScg-linear RGBA colour
 * into the target buffer using the same normal-alpha composite as
 * src/tools/gradient.js / bucket-fill.js (interpolating in scene-linear
 * keeps the renderer's view-transform free to do its job — no baked
 * gamma). Coverage from the scanline rasteriser modulates the alpha so
 * anti-aliased edges blend smoothly.
 *
 * Provenance: clean-room. Scanline fill = Pineda 1988 (via the shared
 * lasso module). Stroke-as-quad-strip is textbook (Hughes/van Dam §15.7,
 * "stroking a curve"). NO Inkscape/GIMP/Krita source consulted.
 */

import { flattenPath, controlBounds } from './bezier.js';
import { setClosed } from './model.js';
import { rasterizeLasso } from '../selection/lasso.js';
import { combine } from '../selection/combine.js';

/* ── Shared ACEScg-normal composite (matches gradient.js::blendPixel) ── */

function blendPixel(rgba, j, r, g, b, a, alpha) {
    if (alpha <= 0) return;
    if (alpha >= 1) {
        rgba[j] = r; rgba[j + 1] = g; rgba[j + 2] = b; rgba[j + 3] = a;
        return;
    }
    const inv = 1 - alpha;
    rgba[j]     = rgba[j]     * inv + r * alpha;
    rgba[j + 1] = rgba[j + 1] * inv + g * alpha;
    rgba[j + 2] = rgba[j + 2] * inv + b * alpha;
    rgba[j + 3] = rgba[j + 3] * inv + a * alpha;
}

function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

/**
 * Composite a coverage mask into an ACEScg RGBA buffer with a flat colour.
 * Optionally gated by a selection mask. `coverage` is reused as scratch.
 */
function compositeCoverage(rgba, w, h, coverage, color, opts) {
    const r = Number(color[0]) || 0;
    const g = Number(color[1]) || 0;
    const b = Number(color[2]) || 0;
    const a = color.length >= 4 ? (Number(color[3]) || 0) : 1;
    const opacity = clamp01(opts && Number.isFinite(opts.opacity) ? opts.opacity : 1);
    const sel = (opts && opts.selectionMask) || null;
    const N = w * h;
    let count = 0;
    for (let i = 0; i < N; i++) {
        let cov = coverage[i];
        if (cov <= 0) continue;
        if (cov > 1) cov = 1;
        if (sel) {
            const sw = sel[i];
            cov *= sw < 0 ? 0 : sw > 1 ? 1 : sw;
            if (cov <= 0) continue;
        }
        blendPixel(rgba, i * 4, r, g, b, a, opacity * cov * a);
        count++;
    }
    return count;
}

/* ── Quad scanline (reuse lasso rasteriser on a 4-vertex polygon) ─────── */

/* A single filled convex quad. We rasterise it into `scratch` via the
 * shared lasso scanline (a quad is just a 4-gon) and max-merge the
 * coverage into `accum` so overlapping stroke quads don't double-darken. */
function accumulateQuad(scratch, accum, w, h, quad, antiAlias) {
    rasterizeLasso(scratch, w, h, quad, antiAlias);
    for (let i = 0; i < w * h; i++) {
        const c = scratch[i];
        if (c > accum[i]) accum[i] = c;
    }
}

/* ── fillPath ────────────────────────────────────────────────────────── */

/**
 * Fill a closed path into an ACEScg RGBA buffer with a flat colour.
 * The path is force-closed (an open path is filled as if its endpoints
 * were joined — same convention as canvas2D `fill()`).
 *
 * @param {Float32Array} rgba   length w*h*4, ACEScg-linear
 * @param {number} w
 * @param {number} h
 * @param {object} path         Bezier path
 * @param {number[]} color      [r,g,b,a] ACEScg-linear
 * @param {{tolerance?:number, antiAlias?:boolean, opacity?:number,
 *          selectionMask?:Float32Array}} [opts]
 * @returns {number} pixels written
 */
export function fillPath(rgba, w, h, path, color, opts = {}) {
    if (!rgba || w <= 0 || h <= 0) throw new Error('fillPath: invalid dims');
    if (rgba.length < w * h * 4) throw new Error('fillPath: buffer too short');
    const tol = Number.isFinite(opts.tolerance) ? opts.tolerance : 0.25;
    const aa  = opts.antiAlias !== false;
    const poly = flattenPath(setClosed(path, true), tol);
    if (poly.length < 3) return 0;
    const cov = new Float32Array(w * h);
    rasterizeLasso(cov, w, h, poly, aa);
    return compositeCoverage(rgba, w, h, cov, color, opts);
}

/* ── strokePath ──────────────────────────────────────────────────────── */

/**
 * Stroke a path's outline into an ACEScg RGBA buffer. The polyline is
 * swept into a strip of quads, each spanning ±halfWidth along the segment
 * normal; round-ish joins emerge naturally because consecutive quads
 * share an edge and overlap at corners. Variable width is supported via
 * a `widthAt(t)` callback (t ∈ [0,1] along the path) — defaults to a
 * constant `width`.
 *
 * @param {Float32Array} rgba
 * @param {number} w
 * @param {number} h
 * @param {object} path
 * @param {number[]} color
 * @param {{width?:number, widthAt?:(t:number)=>number, tolerance?:number,
 *          antiAlias?:boolean, opacity?:number,
 *          selectionMask?:Float32Array}} [opts]
 * @returns {number} pixels written
 */
export function strokePath(rgba, w, h, path, color, opts = {}) {
    if (!rgba || w <= 0 || h <= 0) throw new Error('strokePath: invalid dims');
    if (rgba.length < w * h * 4) throw new Error('strokePath: buffer too short');
    const cov = buildStrokeCoverage(w, h, path, opts);
    if (!cov) return 0;
    return compositeCoverage(rgba, w, h, cov, color, opts);
}

/**
 * Build the stroke coverage mask (no compositing). Exposed so the UI /
 * tests can inspect coverage independently of colour. Returns null when
 * the path is too short to stroke.
 *
 * @returns {Float32Array|null} coverage [0,1], length w*h
 */
export function buildStrokeCoverage(w, h, path, opts = {}) {
    const tol = Number.isFinite(opts.tolerance) ? opts.tolerance : 0.25;
    const aa  = opts.antiAlias !== false;
    const baseWidth = Number.isFinite(opts.width) && opts.width > 0 ? opts.width : 2;
    const widthAt = typeof opts.widthAt === 'function' ? opts.widthAt : null;

    const poly = flattenPath(path, tol);
    if (poly.length < 2) return null;

    /* Cumulative arc-length parameterisation for variable width. */
    const segLen = new Float64Array(poly.length);
    let total = 0;
    for (let i = 1; i < poly.length; i++) {
        const dx = poly[i].x - poly[i - 1].x;
        const dy = poly[i].y - poly[i - 1].y;
        const l = Math.sqrt(dx * dx + dy * dy);
        segLen[i] = l;
        total += l;
    }
    const invTotal = total > 1e-9 ? 1 / total : 0;

    const accum   = new Float32Array(w * h);
    const scratch = new Float32Array(w * h);

    let acc = 0;
    for (let i = 1; i < poly.length; i++) {
        const a = poly[i - 1];
        const b = poly[i];
        const dx = b.x - a.x, dy = b.y - a.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len < 1e-9) { acc += segLen[i]; continue; }

        /* Unit normal (perpendicular to the segment direction). */
        const nx = -dy / len;
        const ny = dx / len;

        /* Per-endpoint half-widths (variable stroke). */
        const tA = acc * invTotal;
        const tB = (acc + len) * invTotal;
        const hwA = (widthAt ? Math.max(0.1, widthAt(tA)) : baseWidth) * 0.5;
        const hwB = (widthAt ? Math.max(0.1, widthAt(tB)) : baseWidth) * 0.5;

        /* Quad corners: A±hwA·n, then B±hwB·n. Wind consistently so the
         * scanline odd-even rule fills the interior. */
        const quad = [
            { x: a.x + nx * hwA, y: a.y + ny * hwA },
            { x: b.x + nx * hwB, y: b.y + ny * hwB },
            { x: b.x - nx * hwB, y: b.y - ny * hwB },
            { x: a.x - nx * hwA, y: a.y - ny * hwA },
        ];
        accumulateQuad(scratch, accum, w, h, quad, aa);

        /* Round-cap each joint with a small square so corners don't gap.
         * A diamond around the vertex (±hw on both axes) overlaps the two
         * adjacent quads, hiding the mitre seam without a true round-join
         * solve (deferred to Wave 12.3). */
        const hw = Math.max(hwA, hwB);
        const diamond = [
            { x: b.x,      y: b.y - hw },
            { x: b.x + hw, y: b.y },
            { x: b.x,      y: b.y + hw },
            { x: b.x - hw, y: b.y },
        ];
        accumulateQuad(scratch, accum, w, h, diamond, aa);

        acc += segLen[i];
    }
    return accum;
}

/* ── pathToMask (Path → Selection) ───────────────────────────────────── */

/**
 * Rasterise a closed path's fill into a fresh Float32 coverage mask, then
 * combine it into `targetMask` via the selection combine ops. This is the
 * "Path → Selection" bridge: the same Pineda scanline that fills pixels
 * also produces a selection coverage map, kept consistent with the
 * marquee/lasso masks.
 *
 * @param {Float32Array} targetMask  existing selection mask (length w*h)
 * @param {number} w
 * @param {number} h
 * @param {object} path
 * @param {string} [op='replace']    combine op (replace/add/subtract/intersect)
 * @param {{tolerance?:number, antiAlias?:boolean}} [opts]
 * @returns {Float32Array} targetMask (mutated)
 */
export function pathToMask(targetMask, w, h, path, op = 'replace', opts = {}) {
    if (!(targetMask instanceof Float32Array)) {
        throw new Error('pathToMask: targetMask must be Float32Array');
    }
    if (targetMask.length !== w * h) {
        throw new Error('pathToMask: targetMask length mismatch');
    }
    const tol = Number.isFinite(opts.tolerance) ? opts.tolerance : 0.25;
    const aa  = opts.antiAlias !== false;
    const poly = flattenPath(setClosed(path, true), tol);
    const scratch = new Float32Array(w * h);
    if (poly.length >= 3) {
        rasterizeLasso(scratch, w, h, poly, aa);
    }
    /* scratch is all-zero when the path is degenerate — combine still
     * applies the op correctly (replace → empty, subtract → no change). */
    return combine(targetMask, scratch, w, h, op);
}

/* ── Convenience: control bounds re-export for the UI sizing math ─────── */

export { controlBounds };
